import {
  hasSubmittedApplication,
  isActiveApplication,
  isTerminalApplication,
} from '@internal/components/projects/helper/application-state';
import { ApplicationState } from '@internal/types/projects';

import { buildBanner, getApplicationListLabel } from './application-banner';
import {
  ApplicationTarget,
  ApplicationViewModel,
  BuildInput,
  StepModel,
  StepStatus,
  StepStatusTone,
} from './application-model.types';
import {
  buildOutcome,
  buildOutcomeFooter,
  buildSideRail,
} from './application-outcome';
import { buildRoleStep } from './application-role-step';

export {
  buildClosedProjectBanner,
  buildOfferBanner,
  getApplicationListLabel,
} from './application-banner';
export type {
  ApplicationAction,
  ApplicationLinkAction,
  ApplicationViewModel,
  BannerModel,
  BuildInput,
  OutcomeFooterModel,
  RoleDetailsModel,
  RoleProgress,
  RoleStageModel,
  SideRailModel,
  StepModel,
  StepStatus,
} from './application-model.types';
export {
  ApplicationTarget,
  isActionDisabled,
  isLinkAction,
  RoleStageStatus,
  ScheduledCallKind,
  StepStatusTone,
} from './application-model.types';
export { getRoleProgress } from './role-progress';

const PAUSED_STATUS: StepStatus = {
  label: 'Paused',
  tone: StepStatusTone.Muted,
};

const pauseStep = (step: StepModel): StepModel =>
  step.done
    ? step
    : { ...step, active: false, status: step.status ?? PAUSED_STATUS };

const buildProfileStep = (
  { profileReady }: BuildInput,
  terminal: boolean,
  rolePrimary: boolean
): StepModel => {
  const primary = !profileReady && !rolePrimary && !terminal;
  return {
    key: 'profile',
    title: 'Review profile',
    text: profileReady
      ? 'Your profile is ready and reused for this application.'
      : 'Confirm the details from your resume.',
    done: profileReady,
    active: primary,
    action: {
      label: 'Go to profile',
      target: ApplicationTarget.Profile,
      primary,
    },
  };
};

const isUnsubmittedCancelledOffer = ({ state, project }: BuildInput): boolean =>
  state === ApplicationState.OfferCancelled &&
  !hasSubmittedApplication(project);

const buildSteps = (input: BuildInput, terminal: boolean): StepModel[] => {
  const roleStep = buildRoleStep(input, terminal);
  if (isUnsubmittedCancelledOffer(input)) return [roleStep];
  return [roleStep, buildProfileStep(input, terminal, roleStep.active)];
};

export const buildApplicationViewModel = (
  input: BuildInput
): ApplicationViewModel => {
  const terminal = isTerminalApplication(input.state);
  const markPaused = (steps: StepModel[]): StepModel[] =>
    input.paused && !terminal ? steps.map(pauseStep) : steps;

  return {
    banner: buildBanner(input),
    statusLabel: getApplicationListLabel(input.state, input),
    steps: markPaused(buildSteps(input, terminal)),
    outcomeTitle: terminal ? 'Application outcome' : 'What happens next',
    outcomeEnded: terminal,
    outcome: markPaused(buildOutcome(input)),
    outcomeFooter: buildOutcomeFooter(input),
    showWithdraw: isActiveApplication(input.state),
    sideRail: buildSideRail(input),
  };
};
